import { CalendarDate, DateValue } from "@internationalized/date";
import { getTrainSeriesFromIdentifier } from "features/History/groupTrainSeries";
import { calendarDateToday } from "features/VehicleSidebar/VehicleList/AddVehicles/VehicleList";
import { CountryCode } from "shared/types/countryCode";
import {
  CombinedHistoryResponse,
  InfrastructureOperationalInformationOverviewResponse,
  TrainOperationalInformationOverviewResponse,
} from "shared/types/operationalInformation";
import { toggle } from "shared/utils/objectUtils";

export const ACTIONABLE_TRAIN_TYPES = ["TRAIN_DELAY_EXPECTED", "TRAIN_STOPPED"];
const GROUP_TRAVEL_TYPE = "TRAIN_GROUP_TRAVEL";
const ASSISTANCE_TYPES = [
  "TRAIN_WHEELCHAIR_PASSENGER",
  "TRAIN_REDUCED_MOBILITY_PASSENGER",
  "TRAIN_VISUALLY_IMPAIRED_PASSENGER",
];
const ENTUR_TYPES = [GROUP_TRAVEL_TYPE, ...ASSISTANCE_TYPES];

function isTrainEvent(
  event: CombinedHistoryResponse,
): event is TrainOperationalInformationOverviewResponse {
  return event.type.group === "TRAIN";
}

function isInfrastructureEvent(
  event: CombinedHistoryResponse,
): event is InfrastructureOperationalInformationOverviewResponse {
  return event.type.group === "INFRASTRUCTURE";
}

type FilterAction =
  | "train"
  | "infrastructure"
  | "station"
  | "global"
  | "internal";
export const filterConditions = [
  "Åpen",
  "Friskmeldt",
  "Må behandles",
  "Erstattet",
  "Depublisert",
] as const;
export type FilterCondition = (typeof filterConditions)[number];

type FilterVisibility = "Synlig" | "Utgått";

const filterRoleTranslations = {
  LOK: "Lok",
  KOND: "Kond",
  ALL: "Lok / Kond",
} as const;
type FilterRole = "LOK" | "KOND" | "ALL";

export const filterRoleToNorwegian = (role: FilterRole) =>
  filterRoleTranslations[role];

export class FilterData {
  actions: FilterAction[] = [];

  searchString: string = "";

  date: DateValue = calendarDateToday();

  groupTravel: boolean = false;

  condition: FilterCondition[] = [];

  visibility: FilterVisibility[] = [];

  trainSeries: string[] = [];

  // TODO(afriestad): maybe extract this? Feels unnecessary to keep around in here
  allTrainIds: string[] = [];

  plannedEvents: boolean = false;

  countryCode: CountryCode = "NO";

  updatedAt: Date = new Date();

  // Same behaviour as .clone
  constructor(init?: Partial<FilterData>) {
    Object.assign(this, init);
  }

  private withOverrides(overrides: Partial<FilterData>): FilterData {
    return new FilterData({ ...this, ...overrides, updatedAt: new Date() });
  }

  static fromString(jsonString: string | null): FilterData | null {
    if (!jsonString) return null;

    const stringObject = JSON.parse(jsonString);
    const [year, month, day] = stringObject.date.split("-");

    return Object.assign(new FilterData(), {
      ...stringObject,
      date: new CalendarDate(
        parseInt(year, 10),
        parseInt(month, 10),
        parseInt(day, 10),
      ),
      updatedAt: new Date(stringObject.updatedAt),
    });
  }

  stringify(): string {
    const dateString = `${this.date.year}-${this.date.month}-${this.date.day}`;
    return JSON.stringify({ ...this, date: dateString });
  }

  isEmpty(): boolean {
    const emptyFilter = new FilterData();
    emptyFilter.allTrainIds = this.allTrainIds;
    return this.stringify() === emptyFilter.stringify();
  }

  updateAction(value: FilterAction): FilterData {
    const nextActions = toggle(this.actions, value);
    return this.withOverrides({ actions: nextActions });
  }

  updateSearchString(value: typeof this.searchString): FilterData {
    return this.withOverrides({ searchString: value });
  }

  updateDate(value: typeof this.date): FilterData {
    return this.withOverrides({ date: value });
  }

  updateGroupTravel(value: typeof this.groupTravel): FilterData {
    const overrides: Partial<FilterData> = { groupTravel: value };
    if (value) overrides.actions = ["train"];
    return this.withOverrides(overrides);
  }

  updatePlannedEvent(value: typeof this.plannedEvents): FilterData {
    return this.withOverrides({ plannedEvents: value });
  }

  updateCondition(value: FilterCondition): FilterData {
    const nextCondition = toggle(this.condition, value);
    return this.withOverrides({ condition: nextCondition });
  }

  updateVisibility(value: FilterVisibility): FilterData {
    const nextVisibility = toggle(this.visibility, value);
    return this.withOverrides({ visibility: nextVisibility });
  }

  updateTrainIds(value: string): FilterData {
    const nextTrainSeries = toggle(this.trainSeries, value);
    const overrides: Partial<FilterData> = { trainSeries: nextTrainSeries };
    if (nextTrainSeries.length > 0) overrides.actions = ["train"];
    return this.withOverrides(overrides);
  }

  updateCountryCode(value: typeof this.countryCode): FilterData {
    return this.withOverrides({ countryCode: value });
  }

  selectAllTrainIds(): FilterData {
    const nextTrainSeries = this.allTrainIds;
    const overrides: Partial<FilterData> = { trainSeries: nextTrainSeries };
    if (nextTrainSeries.length > 0) overrides.actions = ["train"];
    return this.withOverrides(overrides);
  }

  deselectAllTrainIds(): FilterData {
    return this.withOverrides({ trainSeries: [] });
  }

  setAllTrainIds(trainIds: typeof this.allTrainIds): FilterData {
    const deduped = Array.from(
      // TODO(afriestad): optimise with reduce-function
      new Set(trainIds.map(getTrainSeriesFromIdentifier)),
    );
    return this.withOverrides({ allTrainIds: deduped });
  }

  // Remove train series from filter that are no longer in the allTrainIds (e.g. due to date change)
  cleanUpTrainIds(): FilterData {
    const validTrainSeries = this.trainSeries.filter((trainSeries) =>
      this.allTrainIds.includes(trainSeries),
    );
    return this.withOverrides({ trainSeries: validTrainSeries });
  }

  // Filtering mechanism for "filtrer på"
  isMatch(event: CombinedHistoryResponse): boolean {
    const activeFilters = this.actions;
    const eventType = event.type.type;

    if (activeFilters.length > 0) {
      // Handle edge case for global internal info.
      if (
        eventType === "GLOBAL_INTERNAL_INFO" &&
        !activeFilters.includes("internal")
      ) {
        // GLOBAL_INTERNAL_INFO is internal only, should not show up as global
        return false;
      }

      const lowerEventType = eventType.toLowerCase();

      // Do any of the active filters match the event type?
      if (!activeFilters.some((filter) => lowerEventType.includes(filter))) {
        return false;
      }
    }

    // Search string
    if (this.searchString !== "") {
      const message = event.distributions
        .map((it) => it.text.message)
        .join("\n")
        .toLowerCase();

      const title = event.type.label?.toLowerCase();
      const subTitle =
        (isInfrastructureEvent(event)
          ? event.actionCard?.type.toLowerCase()
          : "") ?? "";

      const trainIdentifier =
        (isTrainEvent(event)
          ? event.trainIdentifier.identifier?.toLowerCase()
          : "") ?? "";

      const lineNumber =
        (isTrainEvent(event) ? event.lineNumber?.toLowerCase() : "") ?? "";

      const allText = [
        message,
        title,
        subTitle,
        trainIdentifier,
        lineNumber,
      ].join("\n");

      if (!allText.includes(this.searchString.toLowerCase())) {
        return false;
      }
    }

    // Date: No need to filter since the date field is used to fetch data from backend

    // CountryCode: No need to filter since the country code is used to fetch data from backend

    // Events made by Entur
    if (!this.groupTravel) {
      if (ENTUR_TYPES.includes(event.type.type)) {
        return false;
      }
    }

    // planned infrastructure event
    if (
      !this.plannedEvents &&
      event.type.type.startsWith("INFRASTRUCTURE_PLANNED")
    ) {
      return false;
    }

    // Condition (multi-select)
    if (this.condition.length > 0) {
      const open = event.state === "OPEN";
      const isClosed = event.state === "CLOSED";
      const isReplaced = event.state === "REPLACED";
      const isDepublished = event.state === "DEPUBLISHED";
      const isUnhandled =
        event.state === "OPEN" &&
        ACTIONABLE_TRAIN_TYPES.includes(event.type.type);

      const matches =
        (this.condition.includes("Åpen") && open) ||
        (this.condition.includes("Friskmeldt") && isClosed) ||
        (this.condition.includes("Må behandles") && isUnhandled) ||
        (this.condition.includes("Erstattet") && isReplaced) ||
        (this.condition.includes("Depublisert") && isDepublished);

      if (!matches) {
        return false;
      }
    }

    // Visibility (multi-select)
    if (this.visibility.length > 0) {
      const { isVisible } = event;
      const matchesVisibility =
        (this.visibility.includes("Synlig") && isVisible) ||
        (this.visibility.includes("Utgått") && !isVisible);
      if (!matchesVisibility) {
        return false;
      }
    }

    // Train series
    if (this.trainSeries.length > 0) {
      if (!isTrainEvent(event)) return false;

      const trainSeries = getTrainSeriesFromIdentifier(
        event.trainIdentifier.identifier,
      );

      if (!this.trainSeries.includes(trainSeries)) {
        return false;
      }
    }

    return true;
  }
}
